/**
 * Tree mutation revision policy — single source of truth.
 *
 * For every tree op, declares whether it's revisioned (`true`) or not
 * (`false`). The semantics are binary:
 *
 *   revisioned: true  → IDB, DDB, broadcast, bump workspaceRevision
 *   revisioned: false → IDB, DDB, no broadcast, no bump
 *
 * The frontend is the source of truth. Each request to POST /mutate carries
 * a top-level `revisioned` boolean derived from this map; the backend trusts
 * the flag.
 *
 * To add a new op:
 *   1. Add the op to `MutateTreeOp` in types.ts.
 *   2. Add an entry below — TypeScript will fail until you do.
 */

import type { MutateTreeOp } from './types'

export const OP_POLICY = {
  // Structural — change what the user sees in the layout list / tree.
  create: true,
  delete: true,
  rename: true,
  move: true,
  duplicate: true,
  restore: true,

  // Local-feeling — each tab manages its own.
  set_active: false,
  set_open_state: false,
} as const satisfies Record<MutateTreeOp['op'], boolean>

/**
 * A batch is revisioned if any op in it is. Mixed batches (e.g., delete +
 * set_active when the active layout is removed) are revisioned overall.
 */
export function isRevisionedBatch(ops: MutateTreeOp[]): boolean {
  return ops.some((op) => OP_POLICY[op.op])
}
